import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  DashboardStats,
  OrganizationActivity,
  PaginatedActivities,
  ActivityQueryOptions,
  ActivityNewPayload,
  isActivityVisibleToRole
} from '../models/organization-activity.model';
import { OrganizationContextService } from './organization-context.service';
import { MembershipRoles } from '../models/custom-claims.model';
import { WebSocketService } from './websocket.service';
import { AuthService } from './auth.service';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Dashboard Service
 *
 * Aggregates dashboard statistics and activity data.
 * Manages real-time activity updates via WebSocket.
 *
 * Reference: /agent-os/specs/2026-01-16-dashboard-updates/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardService implements OnDestroy {
  private http = inject(HttpClient);
  private organizationContext = inject(OrganizationContextService);
  private websocketService = inject(WebSocketService);
  private authService = inject(AuthService);

  private apiUrl = environment.apiUrl;
  private destroy$ = new Subject<void>();
  private autoRefreshSubscription: Subscription | null = null;

  // Auto-refresh interval in milliseconds (60 seconds)
  private readonly AUTO_REFRESH_INTERVAL = 60000;

  // Signal-based state management
  private statsSignal = signal<DashboardStats | null>(null);
  private activitiesSignal = signal<OrganizationActivity[]>([]);
  private loadingStatsSignal = signal<boolean>(false);
  private loadingActivitiesSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  private hasMoreSignal = signal<boolean>(false);
  private nextCursorSignal = signal<string | null>(null);

  // Track new activities for animation
  private newActivityIdsSignal = signal<Set<string>>(new Set());

  // Track when user last viewed activities (ISO timestamp)
  private lastViewedActivityAtSignal = signal<string | null>(null);

  // Track if WebSocket subscription is active (prevent duplicates)
  private webSocketSubscribed = false;

  // Maximum activities to show on dashboard
  private readonly MAX_RECENT_ACTIVITIES = 3;

  // Public computed signals for components
  readonly stats$ = computed(() => this.statsSignal());
  readonly activities$ = computed(() => this.activitiesSignal());
  readonly recentActivities$ = computed(() => this.activitiesSignal().slice(0, this.MAX_RECENT_ACTIVITIES));
  readonly isLoadingStats$ = computed(() => this.loadingStatsSignal());
  readonly isLoadingActivities$ = computed(() => this.loadingActivitiesSignal());
  readonly isLoading$ = computed(() => this.loadingStatsSignal() || this.loadingActivitiesSignal());
  readonly error$ = computed(() => this.errorSignal());
  readonly hasMore$ = computed(() => this.hasMoreSignal());
  readonly newActivityIds$ = computed(() => this.newActivityIdsSignal());
  readonly lastViewedActivityAt$ = computed(() => this.lastViewedActivityAtSignal());

  // Computed: count of unseen activities
  readonly unseenActivityCount$ = computed(() => {
    const lastViewed = this.lastViewedActivityAtSignal();
    if (!lastViewed) {
      // If never viewed, all activities are unseen
      return this.activitiesSignal().length;
    }
    const lastViewedDate = new Date(lastViewed);
    return this.activitiesSignal().filter(a => new Date(a.createdAt) > lastViewedDate).length;
  });

  // Computed: whether there are unseen activities (for red dot)
  readonly hasUnseenActivities$ = computed(() => this.unseenActivityCount$() > 0);

  // Computed: unseen activities only
  readonly unseenActivities$ = computed(() => {
    const lastViewed = this.lastViewedActivityAtSignal();
    if (!lastViewed) {
      return this.activitiesSignal();
    }
    const lastViewedDate = new Date(lastViewed);
    return this.activitiesSignal().filter(a => new Date(a.createdAt) > lastViewedDate);
  });

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoRefresh();
    this.unsubscribeFromWebSocket();
  }

  /**
   * Initialize dashboard data - fetches stats, activities, and last viewed timestamp in parallel
   */
  async initialize(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[DashboardService] No organization selected');
      return;
    }

    // Fetch stats, activities, and last viewed in parallel
    await Promise.all([
      this.loadStats(),
      this.loadActivities(),
      this.loadLastViewedActivityAt()
    ]);

    // Start auto-refresh and WebSocket subscription
    this.startAutoRefresh();
    this.subscribeToWebSocket();
  }

  /**
   * Load last viewed activity timestamp from API
   */
  private async loadLastViewedActivityAt(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<{ lastViewedActivityAt: string | null }>>(
          `${this.apiUrl}/organizations/${orgId}/activities/last-viewed`
        )
      );

      if (response.success && response.data) {
        this.lastViewedActivityAtSignal.set(response.data.lastViewedActivityAt);
      }
    } catch (error) {
      // Silently fail - not critical
      console.warn('[DashboardService] Failed to load last viewed timestamp:', error);
    }
  }

  /**
   * Mark activities as seen (updates last viewed timestamp to now)
   */
  async markActivitiesAsSeen(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    const now = new Date().toISOString();

    try {
      await firstValueFrom(
        this.http.post<ApiResponse<{ lastViewedActivityAt: string }>>(
          `${this.apiUrl}/organizations/${orgId}/activities/mark-seen`,
          {}
        )
      );

      // Update local state immediately
      this.lastViewedActivityAtSignal.set(now);
    } catch (error) {
      console.error('[DashboardService] Failed to mark activities as seen:', error);
    }
  }

  /**
   * Load dashboard stats from API
   */
  async loadStats(): Promise<DashboardStats | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[DashboardService] No organization selected');
      return null;
    }

    this.loadingStatsSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<DashboardStats>>(
          `${this.apiUrl}/organizations/${orgId}/dashboard`
        )
      );

      if (response.success && response.data) {
        this.statsSignal.set(response.data);
        return response.data;
      }

      throw new Error(response.error || 'Failed to load dashboard stats');
    } catch (error: any) {
      console.error('[DashboardService] Error loading stats:', error);
      this.errorSignal.set(error.message || 'Failed to load dashboard stats');
      return null;
    } finally {
      this.loadingStatsSignal.set(false);
    }
  }

  /**
   * Load activities from API with optional query options
   */
  async loadActivities(options: ActivityQueryOptions = {}): Promise<PaginatedActivities | null> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('[DashboardService] No organization selected');
      return null;
    }

    this.loadingActivitiesSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = {
        limit: options.limit || 20
      };
      if (options.cursor) params.cursor = options.cursor;
      if (options.eventTypes?.length) params.eventTypes = options.eventTypes.join(',');

      const response = await firstValueFrom(
        this.http.get<ApiResponse<PaginatedActivities>>(
          `${this.apiUrl}/organizations/${orgId}/activities`,
          { params }
        )
      );

      if (response.success && response.data) {
        // If loading more (cursor provided), append to existing
        if (options.cursor) {
          this.activitiesSignal.update(existing => [...existing, ...response.data!.activities]);
        } else {
          this.activitiesSignal.set(response.data.activities);
        }

        this.hasMoreSignal.set(response.data.hasMore);
        this.nextCursorSignal.set(response.data.nextCursor);

        return response.data;
      }

      throw new Error(response.error || 'Failed to load activities');
    } catch (error: any) {
      console.error('[DashboardService] Error loading activities:', error);
      this.errorSignal.set(error.message || 'Failed to load activities');
      return null;
    } finally {
      this.loadingActivitiesSignal.set(false);
    }
  }

  /**
   * Load more activities (pagination)
   */
  async loadMoreActivities(): Promise<void> {
    const cursor = this.nextCursorSignal();
    if (!cursor || this.loadingActivitiesSignal()) return;

    await this.loadActivities({ cursor });
  }

  /**
   * Refresh stats (manual refresh)
   */
  async refreshStats(): Promise<void> {
    await this.loadStats();
  }

  /**
   * Refresh activities (manual refresh)
   */
  async refreshActivities(): Promise<void> {
    this.nextCursorSignal.set(null);
    await this.loadActivities();
  }

  /**
   * Start auto-refresh for stats
   */
  startAutoRefresh(): void {
    this.stopAutoRefresh();

    this.autoRefreshSubscription = interval(this.AUTO_REFRESH_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadStats();
      });
  }

  /**
   * Stop auto-refresh
   */
  stopAutoRefresh(): void {
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
      this.autoRefreshSubscription = null;
    }
  }

  /**
   * Subscribe to WebSocket activity.new events
   */
  subscribeToWebSocket(): void {
    // Prevent duplicate subscriptions
    if (this.webSocketSubscribed) {
      return;
    }
    this.webSocketSubscribed = true;

    this.websocketService.on<ActivityNewPayload>('activity.new')
      .pipe(takeUntil(this.destroy$))
      .subscribe(payload => {
        this.handleNewActivity(payload);
      });
  }

  /**
   * Unsubscribe from WebSocket events
   */
  unsubscribeFromWebSocket(): void {
    // The takeUntil(this.destroy$) handles cleanup
  }

  /**
   * Handle incoming WebSocket activity
   */
  private handleNewActivity(payload: ActivityNewPayload): void {
    // Get user's role for visibility filtering
    const userRole = this.getUserRole();

    // Check if activity is visible to user based on their role
    if (!this.isPayloadVisibleToRole(payload, userRole)) {
      return;
    }

    // Check for duplicate - prevent adding the same activity twice
    const existingActivities = this.activitiesSignal();
    if (existingActivities.some(a => a.id === payload.activityId)) {
      return;
    }

    // Convert payload to OrganizationActivity
    const activity: OrganizationActivity = {
      id: payload.activityId,
      organizationId: this.organizationContext.currentOrganizationId() || '',
      profileId: payload.profileId,
      eventType: payload.eventType,
      message: payload.message,
      resourceType: payload.resourceType,
      resourceId: payload.resourceId,
      visibilityRoles: payload.visibilityRoles,
      isHighPriority: true, // WebSocket events are high priority
      createdAt: payload.createdAt
    };

    // Prepend new activity to list
    this.activitiesSignal.update(existing => [activity, ...existing]);

    // Track as new for animation
    this.newActivityIdsSignal.update(ids => {
      const newIds = new Set(ids);
      newIds.add(activity.id);
      return newIds;
    });

    // Remove from new IDs after animation completes (300ms)
    setTimeout(() => {
      this.newActivityIdsSignal.update(ids => {
        const newIds = new Set(ids);
        newIds.delete(activity.id);
        return newIds;
      });
    }, 350);
  }

  /**
   * Check if payload is visible to user based on their role
   */
  private isPayloadVisibleToRole(payload: ActivityNewPayload, userRole: number): boolean {
    return (payload.visibilityRoles & userRole) !== 0;
  }

  /**
   * Get current user's role for the organization
   * Returns bitwise role value
   */
  private getUserRole(): number {
    const user = this.authService.getCurrentUser();
    const orgId = this.organizationContext.currentOrganizationId();

    if (!user?.customClaims || !orgId) {
      // Default to pilot role if no claims
      return 8; // ROLE_PILOT
    }

    const membership = user.customClaims.getMembership(orgId);
    if (!membership) {
      return 8; // ROLE_PILOT
    }

    // Map MembershipRoles to activity visibility roles
    const orgClaims = membership.claims;

    // Owner (4) or ChiefSupervisor (8) = Owner activity role (1) - sees everything
    if ((orgClaims & MembershipRoles.Owner) !== 0 || (orgClaims & MembershipRoles.ChiefSupervisor) !== 0) {
      return 1; // ROLE_OWNER
    }

    // Pilot (1) or PilotInCharge (2) = Pilot activity role (8)
    return 8; // ROLE_PILOT
  }

  /**
   * Check if an activity is new (for animation)
   */
  isNewActivity(activityId: string): boolean {
    return this.newActivityIdsSignal().has(activityId);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }

  /**
   * Get stats synchronously (for computed signals in components)
   */
  getStats(): DashboardStats | null {
    return this.statsSignal();
  }

  /**
   * Get activities synchronously
   */
  getActivities(): OrganizationActivity[] {
    return this.activitiesSignal();
  }
}
